import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MARKDOWN_COMPONENTS: Components = {
    p: ({ children }) => <p className="my-1">{children}</p>,
    ul: ({ children }) => <ul className="list-disc ml-4 my-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal ml-4 my-1">{children}</ol>,
    li: ({ children }) => <li className="my-0.5">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    code: ({ children }) => <code className="bg-default-100 px-1 rounded text-xs font-mono">{children}</code>,
};

interface ScoreDetail {
    score?: number;
    max_score?: number;
    reasoning?: string;
}

interface GroundedClaim {
    claim?: string;
    supporting_chunks?: string[];
}

interface UngroundedClaim {
    claim?: string;
    reason?: string;
}

interface JudgeAnalysisData {
    overall_score?: number;
    overall_max_score?: number;
    record_id?: string;
    eval_model?: string;
    created_at?: string;
    scores?: Record<string, ScoreDetail>;
    rag_grounding?: {
        grounded_claims?: GroundedClaim[];
        ungrounded_claims?: UngroundedClaim[];
    };
    flags?: Array<{ type?: string; description?: string }>;
    overall_verdict?: {
        summary?: string;
        recommendation?: string;
        recommendation_note?: string;
    };
}

// ─── Mail thread ─────────────────────────────────────────────────────────────

interface ParsedMessage {
    idx: number;
    subject: string;
    fromName: string;
    fromEmail: string;
    to: string;
    cc: string;
    date: string;
    body: string;
}

const MAIL_HEADER_RE = /^(Subject|From|To|Cc|Bcc|Sent|Date):\s*(.*)$/;
const MAIL_FROM_RE = /^([^<]+?)\s*<([^>]+)>/;

function parseMailThread(raw: string): ParsedMessage[] {
    const blocks = raw.split(/\n(?=From:\s)/g).map((b) => b.trim()).filter(Boolean);
    return blocks.map((block, idx) => {
        const headers: Record<string, string> = {};
        const lines = block.split('\n');
        let bodyStart = 0;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const m = MAIL_HEADER_RE.exec(line);
            if (m) {
                headers[m[1].toLowerCase()] = m[2].trim();
            } else if (line.trim() === '' && Object.keys(headers).length > 0) {
                bodyStart = i + 1;
                break;
            }
        }
        const body = lines.slice(bodyStart).join('\n').trim();
        const dateRaw = headers['date'] || headers['sent'] || '';
        const fromMatch = MAIL_FROM_RE.exec(headers['from'] || '');
        const fromName = fromMatch ? fromMatch[1].trim() : (headers['from'] || 'Unknown');
        const fromEmail = fromMatch ? fromMatch[2].trim() : '';
        return {
            idx,
            subject: headers['subject'] || '(no subject)',
            fromName,
            fromEmail,
            to: headers['to'] || '',
            cc: headers['cc'] || '',
            date: dateRaw,
            body,
        };
    });
}

function getInitials(name: string): string {
    return name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

function formatMailDate(s: string): string {
    if (!s) return '';
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
    return s;
}

const MessageCard = ({ msg, isLatest }: { msg: ParsedMessage; isLatest: boolean }) => {
    const [expanded, setExpanded] = useState(isLatest);
    return (
        <div className="relative pl-9">
                        <div className={`absolute left-2 top-[18px] w-3 h-3 rounded-full border-2 z-10 ${isLatest ? 'border-primary bg-primary' : 'border-default-400 bg-content1'}`} />
            <div className={`bg-content1 rounded-[10px] overflow-hidden border ${isLatest ? 'border-primary/40' : 'border-default-200'}`}>
                <button
                    type="button"
                    className="w-full text-left px-4 py-3 bg-content2 border-b border-default-200 cursor-pointer select-none"
                    onClick={() => setExpanded((v) => !v)}
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[11px] font-semibold shrink-0">
                                {getInitials(msg.fromName)}
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-foreground truncate">{msg.fromName}</div>
                                {msg.fromEmail && <div className="text-xs text-default-500 truncate">{msg.fromEmail}</div>}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {isLatest && (
                                <span className="text-[10px] uppercase tracking-wide bg-primary text-white px-2 py-0.5 rounded font-semibold">
                                    Latest
                                </span>
                            )}
                            <span className="text-xs text-default-500">{formatMailDate(msg.date)}</span>
                            <Icon icon="mdi:chevron-down" className={`text-default-400 transition-transform ${expanded ? '' : '-rotate-90'}`} width={16} />
                        </div>
                    </div>
                    {(msg.to || msg.cc) && (
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-default-500">
                            {msg.to && <span><span className="text-default-400 font-medium mr-1">To:</span>{msg.to}</span>}
                            {msg.cc && <span><span className="text-default-400 font-medium mr-1">Cc:</span>{msg.cc}</span>}
                        </div>
                    )}
                    {msg.subject && (
                        <div className="mt-1 text-xs text-default-700">
                            <span className="text-default-400 mr-1">Subject:</span>
                            <span className="font-medium">{msg.subject}</span>
                        </div>
                    )}
                </button>
                {expanded && (
                    <div className="px-4 py-3 text-sm leading-relaxed text-default-700 whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
                        {msg.body}
                    </div>
                )}
            </div>
        </div>
    );
};

const MailThreadModal = ({ isOpen, onClose, mailContent }: { isOpen: boolean; onClose: () => void; mailContent: string }) => {
    const messages = parseMailThread(mailContent);
    const participants = new Set(messages.map((m) => m.fromEmail).filter(Boolean));
    const latestSubject = messages[0]?.subject || '';
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside" isDismissable backdrop="blur">
            <ModalContent>
                <>
                    <ModalHeader className="flex flex-col gap-0.5">
                        <span className="font-medium text-lg">Mail Thread Context</span>
                        <span className="text-sm font-normal text-default-500">Source emails used to ground this advisor response.</span>
                    </ModalHeader>
                    <ModalBody className="px-4 py-4">
                        <div className="flex gap-6 rounded-[10px] border border-default-200 bg-content2 px-4 py-3 mb-4">
                            <div className="flex flex-col gap-0.5">
                                <span className="text-xs text-default-500 font-medium">Messages</span>
                                <span className="text-sm font-semibold text-foreground">{messages.length}</span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-xs text-default-500 font-medium">Participants</span>
                                <span className="text-sm font-semibold text-foreground">{participants.size}</span>
                            </div>
                            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-xs text-default-500 font-medium">Subject</span>
                                <span className="text-sm font-medium text-default-700 truncate">{latestSubject}</span>
                            </div>
                        </div>
                        <div className="relative flex flex-col gap-3">
                            <div className="absolute top-4 bottom-4 left-[14px] w-0.5 bg-default-200 z-0" />
                            {messages.map((msg, i) => (
                                <MessageCard key={`msg-${msg.idx}`} msg={msg} isLatest={i === 0} />
                            ))}
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <CtyButton variant="bordered" size="sm" onPress={onClose}>Close</CtyButton>
                    </ModalFooter>
                </>
            </ModalContent>
        </Modal>
    );
};

// ─── Recommendation config ────────────────────────────────────────────────────

const RECOMMENDATION_CONFIG: Record<string, { label: string; className: string }> = {
    safe_to_show: {
        label: 'Safe to show',
        className: 'text-success bg-success/10 border-success'
    },
    safe_to_show_with_adjustments: {
        label: 'Safe with adjustments',
        className: 'text-warning bg-warning/10 border-warning'
    },
    needs_review: {
        label: 'Needs review',
        className: 'text-warning bg-warning/10 border-warning'
    },
    do_not_show: {
        label: 'Do not show',
        className: 'text-danger bg-danger/10 border-danger'
    }
};

const toTitleCase = (value: string) =>
    value
        .split('_')
        .join(' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

const JudgeAnalysisComponent = ({ judgeAnalysis, mailContent, advisorAnswer, expertAnswer }: { judgeAnalysis?: JudgeAnalysisData; mailContent?: string; advisorAnswer?: string; expertAnswer?: string }) => {
    const { t } = useTranslation();
    const { isOpen: isMailOpen, onOpen: openMail, onClose: closeMail } = useDisclosure();
    const [expandedReasoning, setExpandedReasoning] = useState<Record<string, boolean>>({});
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const displayData = judgeAnalysis ?? {};
    const scores = displayData?.scores ?? {};
    const scoreEntries = Object.entries(scores);
    const groundedClaims = displayData?.rag_grounding?.grounded_claims ?? [];
    const ungroundedClaims = displayData?.rag_grounding?.ungrounded_claims ?? [];

    const aggregateScore = useMemo(() => {
        const totals = scoreEntries.reduce(
            (acc, [, value]) => {
                acc.score += value?.score ?? 0;
                acc.maxScore += value?.max_score ?? 0;
                return acc;
            },
            { score: 0, maxScore: 0 }
        );
        const percentage = totals.maxScore > 0 ? Math.round((totals.score / totals.maxScore) * 100) : 0;
        return { ...totals, percentage };
    }, [scoreEntries]);

    const formattedCreatedAt = useMemo(() => {
        if (!displayData?.created_at) return '-';
        const parsedDate = new Date(displayData.created_at);
        if (Number.isNaN(parsedDate.getTime())) return '-';
        return parsedDate.toLocaleString('en-GB');
    }, [displayData?.created_at]);

    const recommendation = displayData?.overall_verdict?.recommendation ?? '';
    const recommendationConfig = recommendation ? RECOMMENDATION_CONFIG[recommendation] : undefined;
    const recommendationLabel = recommendationConfig?.label ?? 'Not available';
    const recommendationStyle = recommendationConfig?.className ?? 'text-default-700 bg-default-100 border-default-200';
    const verdictSummary = displayData?.overall_verdict?.summary ?? t('No verdict summary available');
    const shouldShowSummaryToggle = verdictSummary.length > 180;
    if (!judgeAnalysis) {
        return <div className="text-sm text-default-500">{t('Judge analysis is not available')}</div>;
    }
    const showAnswerComparison = Boolean(advisorAnswer || expertAnswer);

    return (
        <div className="flex flex-col gap-4 pb-2">

            {showAnswerComparison && (
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {/* Expert Answer */}
                    <div className="rounded-[10px] border border-default-200 bg-content1 flex flex-col">
                        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-default-200 bg-indigo-50 dark:bg-indigo-950 rounded-t-[10px]">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-[#3730a3] text-white flex items-center justify-center text-xs font-bold shrink-0">
                                    E
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-foreground">Expert Answer</div>
                                    <div className="text-xs text-default-500">
                                        {expertAnswer ? `${wordCount(expertAnswer)} words` : '0 words'} · ground truth
                                    </div>
                                </div>
                            </div>
                            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider border border-indigo-200 rounded px-2 py-0.5 text-indigo-700 dark:text-indigo-300 bg-content1">
                                Reference
                            </span>
                        </div>
                        <div className="px-4 py-3 text-sm leading-6 text-default-600 overflow-y-auto max-h-52 flex-1">
                            {expertAnswer || <span className="text-default-400 italic">No expert answer available</span>}
                        </div>
                    </div>

                    {/* Advisor Answer */}
                    <div className="rounded-[10px] border border-default-200 bg-content1 flex flex-col">
                        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-default-200 bg-default-100 rounded-t-[10px]">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-default-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                                    A
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-foreground">Advisor Answer</div>
                                    <div className="text-xs text-default-500">
                                        {advisorAnswer ? `${wordCount(advisorAnswer)} words` : '0 words'} · generated
                                    </div>
                                </div>
                            </div>
                            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider border border-default-300 rounded px-2 py-0.5 text-default-500 bg-content1">
                                Evaluated
                            </span>
                        </div>
                        <div className="px-4 py-3 text-sm leading-6 text-default-600 overflow-y-auto max-h-52 flex-1">
                            {advisorAnswer ? (
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={MARKDOWN_COMPONENTS}
                                >
                                    {advisorAnswer}
                                </ReactMarkdown>
                            ) : (
                                <span className="text-default-400 italic">No advisor answer available</span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="rounded-[10px] border border-default-200 bg-content2 px-4 py-4">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[auto_1fr_auto] lg:items-start lg:gap-4">
                    <div className="lg:pt-1">
                        <div className={`w-fit whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold ${recommendationStyle}`}>
                            {`• ${t(recommendationLabel)}`}
                        </div>
                    </div>
                    <div className="lg:pr-2">
                        <div
                            className={`text-sm leading-7 text-default-700 ${isSummaryExpanded ? '' : 'overflow-hidden'}`}
                            style={isSummaryExpanded ? undefined : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={MARKDOWN_COMPONENTS}
                            >
                                {verdictSummary}
                            </ReactMarkdown>
                        </div>
                        {shouldShowSummaryToggle && (
                            <CtyButton
                                variant="light"
                                size="sm"
                                className="mt-1 h-fit min-w-0 w-fit px-0 !text-[#B1124E] text-xs font-medium normal-case hover:!text-[#8f0f40]"
                                onPress={() => setIsSummaryExpanded((prev) => !prev)}
                            >
                                {isSummaryExpanded ? t('Show less') : t('Show more')}
                            </CtyButton>
                        )}
                    </div>
                    <div className="grid min-w-[220px] grid-cols-1 gap-2 text-xs text-default-500 font-mono lg:justify-items-end lg:text-right">
                        <div>
                            <span className="font-medium">{t('Evaluated at')}: </span>
                            {formattedCreatedAt}
                        </div>
                    </div>
                </div>
            </div>

            <div className="rounded-[10px] border border-default-200 bg-content1 px-5 py-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-end gap-4">
                        <div className="flex items-end gap-2 leading-none">
                            <span className="text-5xl font-semibold text-foreground">{judgeAnalysis.overall_score??aggregateScore.score/4}</span>
                            <span className="pb-1 text-3xl text-default-500">/ {judgeAnalysis.overall_max_score??aggregateScore.maxScore/4}</span>
                        </div>
                        <div className="pb-1">
                            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-default-500">{t('Overall score')}</div>
                            <div className="text-2sm font-medium text-default-600">
                                {t('{{percentage}}% across {{count}} dimensions', {
                                    percentage: aggregateScore.percentage,
                                    count: scoreEntries.length
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-[-4px] text-[11px] font-semibold uppercase tracking-[0.08em] text-default-500">
                {t('Evaluation dimensions')}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {scoreEntries.length > 0 ? (
                    scoreEntries.map(([metricName, metric]) => {
                        const score = metric?.score ?? 0;
                        const maxScore = metric?.max_score ?? 0;
                        const reasoning = metric?.reasoning ?? '';
                        const isExpanded = Boolean(expandedReasoning[metricName]);
                        const shouldCollapse = reasoning.length > 200;
                        const displayReasoning = shouldCollapse && !isExpanded ? `${reasoning.slice(0, 200)}...` : reasoning;
                        const scoreRatio = maxScore > 0 ? score / maxScore : 0;
                        let fillColor = 'bg-danger';
                        if (scoreRatio >= 0.8) {
                            fillColor = 'bg-success';
                        } else if (scoreRatio >= 0.5) {
                            fillColor = 'bg-warning';
                        }

                        return (
                            <div key={metricName} className="rounded-[10px] border border-default-200 bg-content1 p-4">
                                <div className="flex items-center justify-between">
                                    <div className="text-xs font-semibold uppercase tracking-[0.05em] text-default-500">{t(toTitleCase(metricName))}</div>
                                    <div className="font-medium text-sm text-default-700">
                                        {score}
                                        <span className="text-default-500">/{maxScore}</span>
                                    </div>
                                </div>
                                <div className="my-3 flex gap-1">
                                    {Array.from({ length: Math.max(maxScore, 0) }, (_, dotOrder) => dotOrder + 1).map((dotOrder) => (
                                        <span
                                            key={`${metricName}-dot-${dotOrder}`}
                                            className={`h-1.5 flex-1 rounded-full ${dotOrder <= score ? fillColor : 'bg-default-200'}`}
                                        />
                                    ))}
                                </div>
                                <div className="text-sm leading-6 text-default-500">
                                    {displayReasoning || t('No reasoning provided')}
                                </div>
                                {shouldCollapse && (
                                    <CtyButton
                                        variant="light"
                                        size="sm"
                                        className="w-fit h-fit px-0 min-w-0 !text-[#B1124E] text-xs font-medium normal-case hover:!text-[#8f0f40]"
                                        onPress={() => setExpandedReasoning((prev) => ({ ...prev, [metricName]: !isExpanded }))}
                                    >
                                        {isExpanded ? t('Show less') : t('Show more')}
                                    </CtyButton>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="text-sm text-default-500">{t('No score data available')}</div>
                )}
            </div>

            <div className="mb-[-4px] text-[11px] font-semibold uppercase tracking-[0.08em] text-default-500">
                {t('RAG grounding')}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-[10px] border border-default-200 bg-content1 p-4">
                    <div className="mb-3 flex items-center justify-between border-b border-default-200 pb-2">
                        <div className="text-sm font-semibold">{t('Grounded claims')}</div>
                        <div className="rounded-full bg-default-100 px-2 py-0.5 text-xs text-default-500">{groundedClaims.length}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        {groundedClaims.length > 0 ? groundedClaims.map((claim) => (
                            <div key={`grounded-${claim?.claim ?? 'claim'}`} className="rounded-md border-l-[3px] border-success bg-content2 p-3">
                                <div className="text-sm text-default-700">{claim?.claim}</div>
                                {(claim?.supporting_chunks?.length ?? 0) > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {claim.supporting_chunks!.map((chunk) => (
                                            <span key={`chunk-${claim?.claim ?? 'claim'}-${chunk}`} className="text-xs border border-default-200 rounded px-2 py-0.5 bg-content1 text-default-500">
                                                {chunk}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )) : (
                            <div className="text-sm text-default-500">{t('No grounded claims recorded')}</div>
                        )}
                    </div>
                </div>
                <div className="rounded-[10px] border border-default-200 bg-content1 p-4">
                    <div className="mb-3 flex items-center justify-between border-b border-default-200 pb-2">
                        <div className="text-sm font-semibold">{t('Ungrounded claims')}</div>
                        <div className="rounded-full bg-default-100 px-2 py-0.5 text-xs text-default-500">{ungroundedClaims.length}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                        {ungroundedClaims.length > 0 ? ungroundedClaims.map((claim) => (
                            <div key={`ungrounded-${claim?.claim ?? claim?.reason ?? 'claim'}`} className="rounded-md border-l-[3px] border-danger bg-content2 p-3">
                                <div className="text-sm text-default-700">{claim?.claim}</div>
                                {claim?.reason && (
                                    <div className="text-xs text-default-500 mt-1">{claim.reason}</div>
                                )}
                            </div>
                        )) : (
                            <div className="text-sm text-default-500">{t('No ungrounded claims detected')}</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="rounded-[10px] border border-default-200 bg-content2 p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-default-500">{t('Overall verdict')}</div>
                <div className="text-sm leading-6 text-default-700">
                    {displayData?.overall_verdict?.summary ?? t('No verdict summary available')}
                </div>
                {displayData?.overall_verdict?.recommendation_note && (
                    <div className="mt-2 border-t border-dashed border-default-300 pt-2 text-sm text-default-500">
                        <span className="font-medium">{t('Note')}: </span>
                        {displayData.overall_verdict.recommendation_note}
                    </div>
                )}
            </div>

            {mailContent && (
                <div className="flex items-center justify-between gap-4 rounded-[10px] border border-default-200 bg-content2 p-4">
                    <div>
                        <div className="text-sm font-semibold text-default-700">Source Email Thread</div>
                        <div className="text-sm text-default-500 mt-0.5">View the full email conversation used as context for this answer.</div>
                    </div>
                    <CtyButton
                        color="primary"
                        variant="bordered"
                        size="sm"
                        startContent={<Icon icon="mdi:email-outline" width={15} />}
                        onPress={openMail}
                    >
                        Mail Thread
                    </CtyButton>
                </div>
            )}

            {mailContent && <MailThreadModal isOpen={isMailOpen} onClose={closeMail} mailContent={mailContent} />}

        </div>
    );
};

export default JudgeAnalysisComponent;
